import { omit } from 'lodash-es';

type Indexable<V = any> = { [key: string]: V };

type KeysByType<Coll extends {}, Type> = {
  [P in keyof Coll]: Coll[P] extends Type ? P : never;
}[keyof Coll];

export function mapObject<TIn extends Record<string, any>, TMapReturn>(
  object: TIn,
  map: (value: TIn[keyof TIn], key: keyof TIn) => TMapReturn,
) {
  return Object.keys(object).reduce<Record<string, TMapReturn>>((result, key) => {
    result[key] = map(object[key], key as keyof TIn);

    return result;
  }, {}) as Record<keyof TIn, TMapReturn>;
}

export const mapObjectByProperty = <T extends Indexable<Indexable>, P extends keyof T[keyof T]>(
  obj: T,
  prop: P,
): { [K in keyof T]: T[K][P] } => {
  const entries = Object.entries(obj).map(([k]) => [k, obj[k as keyof typeof obj][prop]]);
  return Object.fromEntries(entries);
};

export const excludeObjectUndefinedValues = <T extends Indexable>(
  obj: T,
): Omit<T, KeysByType<T, undefined>> => {
  return Object.keys(obj).reduce((acc, key: keyof T) => {
    if (obj[key] !== undefined) acc[key] = obj[key];
    return acc;
  }, {} as T);
};

export const defaultValue = <T>(val: T, defaultProp: T): T =>
  val === undefined ? defaultProp : val;

export const sortObjectByKeys = <T extends Indexable>(obj: T, recursive = false): T => {
  const entries = Object.entries(obj).sort(([a], [b]) =>
    a.toLowerCase() < b.toLowerCase() ? -1 : 1,
  );

  const sorted = Object.fromEntries(entries) as T;

  if (recursive) {
    for (const key in sorted) {
      if (typeof sorted[key] === 'object' && sorted[key] !== null) {
        sorted[key] = sortObjectByKeys(sorted[key], true);
      }
    }
  }

  return sorted;
};

export const objectKeys = <T extends Indexable>(obj: T) => {
  return Object.keys(obj) as (keyof T)[];
};

export function getRestProps<T extends Indexable, K extends keyof T>(props: T, ...keys: K[]) {
  return omit(props, keys);
}

export function getSiblingPrev<T extends Record<string, any>, K extends keyof T>(
  obj: T,
  key: K,
): [keyof T, T[keyof T]] | undefined {
  const keys = objectKeys(obj);
  const index = keys.indexOf(key);

  if (index > 0) {
    const prevKey = keys[index - 1];
    return [prevKey, obj[prevKey]];
  }

  return undefined;
}

export function getSiblingNext<T extends Record<string, any>, K extends keyof T>(
  obj: T,
  key: K,
): [keyof T, T[keyof T]] | undefined {
  const keys = Object.keys(obj) as Array<keyof T>;
  let foundKey = false;

  for (const currentKey of keys) {
    if (foundKey) {
      return [currentKey, obj[currentKey]];
    }
    if (currentKey === key) {
      foundKey = true;
    }
  }

  return undefined;
}
